import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import CheckIcon from '@heroicons/react/outline/CheckIcon';
import XIcon from '@heroicons/react/outline/XIcon';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';
import { CustomFieldType, } from '@tribeplatform/gql-client/types';
import { useRouter } from '@tribeplatform/react-sdk';
import { List } from '@tribeplatform/react-ui-kit/Layout';
import { MediaGallery, MediaZoom } from '@tribeplatform/react-ui-kit/MediaZoom';
import { Truncate } from '@tribeplatform/react-ui-kit/Truncate';
import { FileListItem } from '../common/components/FileListItem';
import { dayjs } from '../common/lib/dayjs';
import { HtmlContent } from '../HtmlContent';
import { useI18n } from '../i18n';
import { MemberPill } from '../Member/MemberPill';
import { SpacePill } from '../Space/SpacePill';
import { ButtonView } from './components/ButtonView';
import { DateView } from './components/DateView';
import { LocationView } from './components/LocationView';
import { PillsView } from './components/PillsView';
import { PillView } from './components/PillView';
import { RatingView } from './components/RatingView';
import { SocialMediaView } from './components/SocialMediaView';
import { TitleView } from './components/TitleView';
import { FieldPrivacyBadges } from './FieldPrivacyBadges';
import { CustomFieldSubtype, CustomFieldViews, getFieldSetting, TRUNCATE_SCROLL_OFFSET, } from './utils';
const getSampleFile = name => ({
    name,
    extension: 'pdf',
    size: 50000,
});
const getSampleImage = path => ({
    url: path,
    urls: {
        full: path,
        large: path,
        medium: path,
        thumb: path,
    },
});
export const getFieldPreviewValue = ({ view, field, subtype }) => {
    var _a, _b;
    let options = [];
    const getValidatorsEnum = () => {
        var _a, _b;
        try {
            const validators = ((_a = field === null || field === void 0 ? void 0 : field.items) === null || _a === void 0 ? void 0 : _a.validators) || (field === null || field === void 0 ? void 0 : field.validators);
            options = JSON.parse((_b = validators === null || validators === void 0 ? void 0 : validators.find(v => v.validation === 'enum')) === null || _b === void 0 ? void 0 : _b.value);
        }
        catch (e) {
            console.error('Error parsing network entity field value', e.message);
        }
        return options || [];
    };
    switch (view) {
        case CustomFieldViews.Title:
            return 'Lorem sunt excepteur aliquip';
        case CustomFieldViews.Button:
            return '#';
        case CustomFieldViews.Date:
            return dayjs().startOf('hour').toDate();
        case CustomFieldViews.Location:
            return 'New York, USA';
        case CustomFieldViews.Rating:
            return 4;
        default:
    }
    switch (field === null || field === void 0 ? void 0 : field.type) {
        case CustomFieldType.richText:
            return '<p>Commodo <b>magna esse</b> ea in in. Minim <i>occaecat</i> aute <a>veniam pariatur</a> laboris veniam.</p>';
        case CustomFieldType.number:
            return 1234;
        case CustomFieldType.boolean:
            return true;
        case CustomFieldType.date:
            return dayjs().startOf('hour').toDate();
        case CustomFieldType.array:
            switch (subtype) {
                case CustomFieldSubtype.MULTISELECT:
                    options = (_a = getValidatorsEnum()) === null || _a === void 0 ? void 0 : _a.slice(0, 2);
                    return (options === null || options === void 0 ? void 0 : options.length) ? options : ['Sample', 'Value'];
                case CustomFieldSubtype.IMAGES:
                    return [
                        getSampleImage('https://tribe-s3-production.imgix.net/NflFD6u3VW1YABpEJdeeb?w=2000&auto=compress,format&dl'),
                        getSampleImage('https://tribe-s3-production.imgix.net/NflFD6u3VW1YABpEJdeeb?w=2000&auto=compress,format&dl'),
                        getSampleImage('https://tribe-s3-production.imgix.net/NflFD6u3VW1YABpEJdeeb?w=2000&auto=compress,format&dl'),
                    ];
                case CustomFieldSubtype.FILES:
                    return [getSampleFile('Sample file')];
                case CustomFieldSubtype.MEMBERS:
                    return [{ name: 'Jane Smith' }, { name: 'John Smith' }];
                case CustomFieldSubtype.SPACES:
                    return [{ name: 'Space A' }, { name: 'Space B' }];
                default:
                    return [];
            }
        case CustomFieldType.text:
            switch (subtype) {
                case CustomFieldSubtype.URL:
                    return 'https://example.com';
                case CustomFieldSubtype.EMAIL:
                    return 'info@example.com';
                case CustomFieldSubtype.TEXTAREA:
                    return 'Lorem sunt excepteur aliquip commodo deserunt pariatur.';
                case CustomFieldSubtype.SELECT:
                    return ((_b = getValidatorsEnum()) === null || _b === void 0 ? void 0 : _b[0]) || 'Sample value';
                default:
                    return 'Sample value';
            }
        case CustomFieldType.relation:
            switch (subtype) {
                case CustomFieldSubtype.IMAGE:
                    return getSampleImage('https://tribe-s3-production.imgix.net/NflFD6u3VW1YABpEJdeeb?w=2000&auto=compress,format&dl');
                case CustomFieldSubtype.FILE:
                    return getSampleFile('Sample file');
                case CustomFieldSubtype.SPACE:
                    return { name: 'Test space' };
                case CustomFieldSubtype.MEMBER:
                    return { name: 'Jane Smith' };
                default:
                    return null;
            }
        default:
    }
};
const isEmpty = (value, type) => {
    if (type === CustomFieldType.boolean) {
        return value === null || typeof value === 'undefined';
    }
    return !value;
};
export const FieldView = ({ field, entity, highlighted = false, preview = false, showLabel = false, collapsible = false, className = '', allowEmpty = false, context = null, }) => {
    var _a, _b, _c, _d, _e, _f;
    const { $t } = useI18n();
    const { Link } = useRouter();
    if (field === null || field === void 0 ? void 0 : field.archived) {
        return null;
    }
    let entityField = (_a = entity === null || entity === void 0 ? void 0 : entity.fields) === null || _a === void 0 ? void 0 : _a.find(f => f.key === field.key);
    if (!entityField) {
        entityField = { key: field.key, value: null };
    }
    let { value } = entityField;
    // Fallback for scaffolding. Can be removed after migration
    if (value === JSON.stringify(null)) {
        if ((field === null || field === void 0 ? void 0 : field.key) === 'title') {
            entityField = (_b = entity === null || entity === void 0 ? void 0 : entity.mappingFields) === null || _b === void 0 ? void 0 : _b.find(f => f.key === 'title' || f.key === 'question');
        }
        else if ((field === null || field === void 0 ? void 0 : field.key) === 'content') {
            entityField = (_c = entity === null || entity === void 0 ? void 0 : entity.mappingFields) === null || _c === void 0 ? void 0 : _c.find(f => f.key === 'content' || f.key === 'description' || f.key === 'answer');
        }
        value = entityField === null || entityField === void 0 ? void 0 : entityField.value;
    }
    if (value) {
        try {
            value = JSON.parse(value);
        }
        catch (e) {
            console.error('Error parsing network entity field value', e.message);
        }
    }
    if ((typeof value === 'undefined' || value === null) && !preview) {
        if (allowEmpty) {
            return _jsx("div", { className: className }, void 0);
        }
        return null;
    }
    const view = getFieldSetting(field, 'view') || getFieldSetting(field, 'viewComponent');
    const viewProps = getFieldSetting(field, 'viewProps') || {};
    const subtype = getFieldSetting(field, 'subtype');
    const viewMapping = {
        Button: ButtonView,
        Date: DateView,
        SocialMedia: SocialMediaView,
        Title: TitleView,
        Pill: PillView,
        Pills: PillsView,
        Location: LocationView,
        Rating: RatingView,
    };
    const Component = viewMapping[view];
    let isSample = false;
    let result = null;
    if (isEmpty(value, field === null || field === void 0 ? void 0 : field.type)) {
        if (preview) {
            value = getFieldPreviewValue({ view, field, subtype });
            isSample = true;
        }
        else {
            if (allowEmpty) {
                return _jsx("div", { className: className }, void 0);
            }
            return null;
        }
    }
    if (field.type === CustomFieldType.date) {
        if (viewProps.format === 'relative') {
            value = dayjs.utc(value).fromNow();
        }
        else if (viewProps.format === 'standard') {
            if (subtype === CustomFieldSubtype.DATETIME) {
                value = dayjs.utc(value).tz().format('ddd, MMM D, h:mma');
            }
            else {
                value = dayjs(value).format('ddd, MMM D');
            }
        }
        else if (subtype === CustomFieldSubtype.DATETIME) {
            value = dayjs.utc(value).tz().format('MM/DD/YYYY, h:mma');
        }
        else {
            value = dayjs(value).format('MM/DD/YYYY');
        }
    }
    let flatRelationEntities = null;
    if (entityField === null || entityField === void 0 ? void 0 : entityField.relationEntities) {
        flatRelationEntities = Object.keys(entityField === null || entityField === void 0 ? void 0 : entityField.relationEntities).reduce((result, key) => {
            var _a, _b;
            return [
                ...result,
                ...(Array.isArray((_a = entityField === null || entityField === void 0 ? void 0 : entityField.relationEntities) === null || _a === void 0 ? void 0 : _a[key])
                    ? (_b = entityField === null || entityField === void 0 ? void 0 : entityField.relationEntities) === null || _b === void 0 ? void 0 : _b[key]
                    : []),
            ];
        }, []);
        if (!(flatRelationEntities === null || flatRelationEntities === void 0 ? void 0 : flatRelationEntities.length)) {
            flatRelationEntities = null;
        }
    }
    if (Component) {
        result = (_jsx(Component, { field: field, value: value, entity: entity, context: context, ...viewProps }, void 0));
        if (Component === null || Component === void 0 ? void 0 : Component.skipLabel) {
            showLabel = false;
        }
    }
    else {
        if (field.type === CustomFieldType.array) {
            if (subtype === CustomFieldSubtype.SELECT) {
                result = (_d = value === null || value === void 0 ? void 0 : value.map(option => {
                    return option;
                })) === null || _d === void 0 ? void 0 : _d.join(', ');
            }
            else {
                const entities = flatRelationEntities || value || [];
                if (entities.length) {
                    if (subtype === CustomFieldSubtype.IMAGES) {
                        result = (_jsx(MediaZoom, { zoom: true, download: true, children: _jsx(MediaGallery, { className: "my-2 rounded-md overflow-hidden", images: entities.map(entity => {
                                    var _a, _b;
                                    return {
                                        id: entity === null || entity === void 0 ? void 0 : entity.id,
                                        thumbnail: (_a = entity === null || entity === void 0 ? void 0 : entity.urls) === null || _a === void 0 ? void 0 : _a.medium,
                                        original: (_b = entity === null || entity === void 0 ? void 0 : entity.urls) === null || _b === void 0 ? void 0 : _b.large,
                                        alt: 'Image',
                                    };
                                }) }, void 0) }, void 0));
                    }
                    else if (subtype === CustomFieldSubtype.FILES) {
                        result = (_jsx(List, { className: "flex-1 min-w-0", children: entities.map(entity => (_jsx(List.Item, { children: _jsx(FileListItem, { ...entities === null || entities === void 0 ? void 0 : entities[0] }, void 0) }, (entity === null || entity === void 0 ? void 0 : entity.id) || (entity === null || entity === void 0 ? void 0 : entity.name)))) }, void 0));
                    }
                    else if (subtype === CustomFieldSubtype.MEMBERS) {
                        result = (_jsx("div", { className: "flex space-s-2 flex-wrap", children: entities.map(entity => (_jsx(MemberPill, { member: entity }, entity.id))) }, void 0));
                    }
                    else if (subtype === CustomFieldSubtype.SPACES) {
                        result = (_jsx("div", { className: "flex space-s-2 flex-wrap", children: entities.map(entity => (_jsx(SpacePill, { space: entity }, entity.id))) }, void 0));
                    }
                    else {
                        value = null;
                    }
                }
                else {
                    value = null;
                }
            }
        }
        else if (field.type === CustomFieldType.number) {
            if (typeof (value === null || value === void 0 ? void 0 : value.toLocaleString) === 'function') {
                result = value.toLocaleString();
            }
        }
        else if (field.type === CustomFieldType.boolean) {
            result =
                value === true ? (_jsx(CheckIcon, { className: "w-4 h-4 text-content-on-positive" }, void 0)) : (_jsx(XIcon, { className: "w-4 h-4 text-action-destructive" }, void 0));
        }
        else if (field.type === CustomFieldType.richText) {
            if (collapsible) {
                result = (_jsx(Truncate, { size: "md", scrollOffset: TRUNCATE_SCROLL_OFFSET, seeMoreText: $t({
                        defaultMessage: 'See more',
                        id: 'Generics.SeeMore',
                    }), seeLessText: $t({
                        defaultMessage: 'See less',
                        id: 'Generics.SeeLess',
                    }), gradientTo: context === 'pinnedReply' ? 'to-surface-positive' : undefined, children: _jsx("article", { className: "text-base text-content", children: _jsx(HtmlContent, { value: value, attachments: entity === null || entity === void 0 ? void 0 : entity.attachments, embeds: entity === null || entity === void 0 ? void 0 : entity.embeds, mentions: entity === null || entity === void 0 ? void 0 : entity.mentions }, void 0) }, void 0) }, void 0));
            }
            else {
                result = (_jsx("article", { className: "text-base text-content", children: _jsx(HtmlContent, { value: value, attachments: entity === null || entity === void 0 ? void 0 : entity.attachments, embeds: entity === null || entity === void 0 ? void 0 : entity.embeds, mentions: entity === null || entity === void 0 ? void 0 : entity.mentions }, void 0) }, void 0));
            }
        }
        else if (field.type === CustomFieldType.text) {
            const subtype = getFieldSetting(field, 'subtype');
            if (subtype === CustomFieldSubtype.URL) {
                result = (_jsx(Link, { className: "truncate", href: value, variant: "neutral", onClick: e => e.stopPropagation(), children: value }, void 0));
            }
            else if (subtype === CustomFieldSubtype.EMAIL) {
                result = (_jsx(Link, { className: "truncate", href: `mailto:${value}`, target: "_blank", variant: "neutral", onClick: e => e.stopPropagation(), children: value }, void 0));
            }
        }
        else if (field.type === CustomFieldType.relation) {
            const entities = flatRelationEntities || [value] || [];
            if (entities.length) {
                if (subtype === CustomFieldSubtype.IMAGE) {
                    const media = entities === null || entities === void 0 ? void 0 : entities[0];
                    result = (_jsx(MediaZoom, { zoom: true, download: true, children: _jsx(MediaZoom.Item, { className: "rounded-md overflow-hidden my-2", item: {
                                id: media === null || media === void 0 ? void 0 : media.id,
                                thumbnail: (_e = media === null || media === void 0 ? void 0 : media.urls) === null || _e === void 0 ? void 0 : _e.large,
                                original: (_f = media === null || media === void 0 ? void 0 : media.urls) === null || _f === void 0 ? void 0 : _f.large,
                                alt: 'Cover',
                            } }, void 0) }, void 0));
                }
                else if (subtype === CustomFieldSubtype.MEMBER) {
                    const member = entities === null || entities === void 0 ? void 0 : entities[0];
                    result = _jsx(MemberPill, { member: member }, void 0);
                }
                else if (subtype === CustomFieldSubtype.SPACE) {
                    const space = entities === null || entities === void 0 ? void 0 : entities[0];
                    result = _jsx(SpacePill, { space: space }, void 0);
                }
                else if (subtype === CustomFieldSubtype.FILE) {
                    result = _jsx(FileListItem, { ...entities === null || entities === void 0 ? void 0 : entities[0] }, void 0);
                }
                if (!value) {
                    value = null;
                }
            }
            else {
                value = null;
            }
        }
        if (!result) {
            result = value;
        }
    }
    if (!showLabel) {
        return (_jsx("div", { className: twMerge(clsx('mt-1 text-content', highlighted && 'ring-2 rounded-sm ring-offset-surface ring-focused', subtype === CustomFieldSubtype.URL && 'truncate', subtype === CustomFieldSubtype.EMAIL && 'truncate', className)), children: result }, void 0));
    }
    return (_jsxs("div", { className: twMerge(clsx('web:sm:col-span-1 space-y-1', highlighted && 'ring-2 rounded-sm ring-offset-surface ring-focused'), className), children: [_jsxs("dt", { className: "font-medium text-content-subdued flex space-s-1 items-center", children: [_jsx("span", { children: field.name }, void 0), _jsx(FieldPrivacyBadges, { field: field }, void 0)] }, void 0), _jsx("dd", { className: clsx('text-content', field.type === CustomFieldType.text && 'min-w-0 truncate', field.type !== CustomFieldType.text && 'flex items-center', isSample && 'opacity-50'), children: result }, void 0)] }, entityField.key));
};
//# sourceMappingURL=FieldView.js.map